import React from 'react';
import { IStorefrontSection } from '../../../../models/Storefront';
import { IStorefrontProductView } from '../../../../models/StorefrontProducts';
import { useGetStorefrontSectionProductsQuery, useLazyGetStorefrontProductDetailQuery } from '../../../../store/endpoints/StorefrontProductEndpoints';

// Client-side resolution of a Selected Products section's productIds, so the editor (WYSIWYG
// preview + settings panel) reflects adds/removes IMMEDIATELY instead of only after save:
// - Saved sections seed from the section-products endpoint (one call, shared RTK cache).
// - Ids missing from that baseline (added this session, or a section not saved yet) resolve
//   one-by-one through the product detail endpoint against the LIVE storefront — every candidate
//   came from the live-catalog admin search, so that's where it's listed.
// - Ids that fail to resolve (unlisted/deleted products) report as failed so callers can show the
//   raw id instead of a spinner forever.

export interface IResolvedSectionProducts {
    // section.productIds mapped to resolved views, in order, unresolved ids skipped.
    products: IStorefrontProductView[];
    resolve: (id: string) => IStorefrontProductView | undefined;
    failed: (id: string) => boolean;
    // True while the saved-section baseline is still loading (per-id fetches surface through
    // resolve() returning undefined with failed() false).
    loading: boolean;
}

export const useResolvedSectionProducts = (
    storefrontId: string | undefined,
    section: IStorefrontSection,
    // Optional pre-resolved views (e.g. the panel's just-picked search hits) — treated as already
    // resolved so no detail fetch is issued for them.
    knownProducts?: Record<string, IStorefrontProductView>,
): IResolvedSectionProducts => {
    const productIds = React.useMemo(() => section.productIds ?? [], [section.productIds]);
    const liveStorefrontId = (storefrontId ?? '').replace(/-preview$/, '');
    const unsaved = section.isNew || !section.id || section.id.startsWith('new-');

    const saved = useGetStorefrontSectionProductsQuery(
        { storefrontId: storefrontId ?? '', sectionId: section.id ?? '' },
        { skip: !storefrontId || unsaved }
    );

    const [fetchDetail] = useLazyGetStorefrontProductDetailQuery();
    // null marks a failed lookup so we don't refetch it every render.
    const [fetched, setFetched] = React.useState<Record<string, IStorefrontProductView | null>>({});
    const inFlight = React.useRef<Set<string>>(new Set());

    React.useEffect(() => {
        if (!liveStorefrontId) return;
        // Wait for the baseline on saved sections so we don't detail-fetch ids it already covers.
        if (!unsaved && saved.data == null) return;
        const baseline = new Set((saved.data?.items ?? []).map(p => p.id));
        productIds.forEach((id) => {
            if (baseline.has(id) || knownProducts?.[id] || fetched[id] !== undefined || inFlight.current.has(id)) return;
            inFlight.current.add(id);
            fetchDetail({ storefrontId: liveStorefrontId, productId: id }, true).unwrap()
                .then((product) => setFetched((m) => ({ ...m, [id]: product })))
                .catch(() => setFetched((m) => ({ ...m, [id]: null })))
                .finally(() => inFlight.current.delete(id));
        });
    }, [liveStorefrontId, unsaved, productIds, saved.data, knownProducts, fetched, fetchDetail]);

    const resolve = React.useCallback((id: string): IStorefrontProductView | undefined =>
        knownProducts?.[id]
        ?? saved.data?.items?.find(p => p.id === id)
        ?? fetched[id]
        ?? undefined,
    [knownProducts, saved.data, fetched]);

    const products = React.useMemo(
        () => productIds.map(resolve).filter((p): p is IStorefrontProductView => p != null),
        [productIds, resolve]);

    return {
        products,
        resolve,
        failed: (id: string) => fetched[id] === null,
        loading: saved.isLoading || saved.isFetching,
    };
};
